import { createContext, useCallback, useEffect, useState } from 'react'
import { useGenericStrictContext } from 'utils/createContext'
import { CIMSTableProps } from '../types'
import { ColumnType, ColumnsType } from 'antd/lib/table'
import { useProminentCustomFields } from '../../CustomFields/hooks/useProminentCustomFields'
import { ColumnGroupType } from 'antd/lib/table/interface'
import { CIMSColumn } from '../CIMSColumn/types'
import CIMSTableCell from './components/CIMSTableCell'
import { useCIMSModalValues } from '../../CIMSModal/hooks/useCIMSModal'
import SimpleEditModal from '../components/SimpleEditModal'
import SimpleCreateModal from '../components/SimpleCreateModal'
import { CIMSTableContext } from './types'
import { useExpandableSection } from './hooks/useExpandableSection'
import { setSortOptions } from './utils/setSortOptions'
import { useSetFilterOptions } from './hooks/useSetFilterOptions'
import { createBaseColumn } from './hooks/createBaseColumn'
import { useActiveColumnFn } from './hooks/useActiveColumnFn'
import { useActionsColumnFn } from './hooks/useActionsColumnFn'
import { useMiscellaneousHeaderActions } from './hooks/useMiscellaneousHeaderActions'
import { useGetWidth } from './hooks/useGetWidth'
import { value } from './utils/value'

const Context = createContext(null)

export function useCIMSTableContext<T extends object, U = any>() {
  return useGenericStrictContext<Readonly<CIMSTableContext<T, U>>>(
    Context,
    'CIMSTableContext'
  )
}

function CIMSTableProvider<RecordType extends object, InitialValues = any>({
  rowKey,
  records,
  actions,
  children,
  defaultSort,
  expand,
  disable,
  ...props
}: React.PropsWithChildren<CIMSTableProps<RecordType, InitialValues>>) {
  const [loading, setLoading] = useState(false)
  useEffect(() => {
    setLoading(props.loading ?? false)
  }, [props.loading])

  /**
   * i cannot find a way to move this callback into it's own hook without causing
   * a max-depth error in React.
   *
   * I suspect it's because we're doing recursion for column groups and I can't
   * find the right way to set up the use callback dependencies
   */
  const setFilterOptions = useSetFilterOptions(records)
  const getWidth = useGetWidth(records)
  const toColumn = useCallback<
    (column: CIMSColumn<RecordType>) => ColumnsType<RecordType>[number]
  >(
    ({ filter, ...initial }) => {
      let column
      if (initial.type === 'group') {
        const columns = initial.columns
        column = {
          ...createBaseColumn(initial),
          children: columns.map(toColumn),
        } as ColumnGroupType<RecordType>
      } else {
        const { render, align, sort, width } = initial

        column = {
          ...createBaseColumn(initial),
          align,
          width: getWidth(width),
          render: (value, record, index) => (
            <CIMSTableCell>
              {typeof render === 'function' ? render(record, index) : value}
            </CIMSTableCell>
          ),
        } as ColumnType<RecordType>
        if (typeof render !== 'function') {
          column.dataIndex = render
          column.key = render
        }
        if (sort) setSortOptions(column, sort)
      }

      if (filter) setFilterOptions(column, filter)

      return column
    },
    [getWidth, setFilterOptions]
  )
  const activeColumn = useActiveColumnFn(actions)
  const actionsColumn = useActionsColumnFn({ records, rowKey, actions })

  const [dataSource, setDataSource] = useState<RecordType[]>([])
  const { expandable, showCollapseButton, setExpandedRowKeys } =
    useExpandableSection({ dataSource, rowKey, expand })
  const miscHeaderActions = useMiscellaneousHeaderActions({
    records,
    rowKey,
    setDataSource,
    setExpandedRowKeys,
    actions,
    defaultSort,
  })
  const prominent = useProminentCustomFields(
    Array.isArray(props.columns)
      ? {
          records: [],
          disableEditWhen: () => ({ alreadyDisabled: false }),
          onCheckBoolean: () => {},
          onCheckBooleans: () => {},
        }
      : { ...props.columns.prominentCustomFields, records }
  )

  const { state: editState, ...simpleEdit } = useCIMSModalValues<RecordType>()
  const { state: _ignored, ...simpleCreate } = useCIMSModalValues<null>()
  return (
    <Context.Provider
      value={
        value({
          disable,
          modals: { simpleEdit, simpleCreate },
          columns: activeColumn(
            actionsColumn(
              [
                ...(Array.isArray(props.columns)
                  ? props.columns
                  : props.columns.columns),
                ...prominent,
              ].map(toColumn)
            )
          ),
          dataSource,
          loading,
          setLoading,
          expandable,
          rowKey,
          actions: {
            row: actions?.row,
            header: {
              ...actions?.header,
              ...miscHeaderActions,
              collapse: {
                visible: showCollapseButton,
                onCollapse: () => setExpandedRowKeys([]),
              },
            },
          },
          // we're doing this on purpose because you can't use createStrictContext with generics
        }) as unknown as null
      }
    >
      <SimpleEditModal
        onClose={() => simpleEdit.toggle(null)}
        visible={simpleEdit.visible}
        state={editState}
      />
      <SimpleCreateModal
        onClose={() => simpleCreate.toggle(null)}
        visible={simpleCreate.visible}
      />
      {children}
    </Context.Provider>
  )
}

export default CIMSTableProvider
